import { useList } from '@refinedev/core';
import { useState } from 'react';
import { FormField } from '@/components/forms';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TranslationsForm, type TranslationLanguage } from '@/components/translations/TranslationsForm';
import { LanguageSwitcher } from '@/components/translations/LanguageSwitcher';
import type { TranslationData, TranslationFieldConfig } from '@/components/translations/translations-data';
import type { TriggerAction } from '../../types';

interface EmailFormProps {
  action: TriggerAction;
  onChange: (patch: Partial<TriggerAction>) => void;
}

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
  flagEmoji?: string;
};

const TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'subject', label: 'Тема письма', type: 'input' },
  { name: 'body', label: 'Тело письма', type: 'textarea', required: true, rows: 4 },
];

/**
 * EmailForm
 *
 * Форма действия отправки Email с поддержкой мультиязычности
 */
export function EmailForm({ action, onChange }: EmailFormProps) {
  const { result: langResult } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: TranslationLanguage[] = (langResult?.data ?? []).map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flag ?? l.flagEmoji ?? '🏳️',
  }));

  const [activeLanguage, setActiveLanguage] = useState('ru');

  const showTranslations = languages.length > 0;

  // Конвертируем translations из action в TranslationData формат
  // ru берём из основных полей subject/body (они являются дефолтом)
  const translationsData: TranslationData = {
    ...(action.subject || action.body ? {
      ru: {
        ...(action.subject ? { subject: action.subject } : {}),
        ...(action.body ? { body: action.body } : {}),
      },
    } : {}),
    ...action.translations,
  };

  const handleTranslationsChange = (data: TranslationData) => {
    const { ru: ruData, ...otherLangs } = data;

    // Синхронизируем ru → основные поля
    const patch: Partial<TriggerAction> = {};
    if (ruData) {
      if (ruData.subject !== undefined) { patch.subject = ruData.subject || undefined; }
      if (ruData.body !== undefined) { patch.body = ruData.body || undefined; }
    }

    // Убираем пустые языки из translations (без ru)
    const cleaned: Record<string, Record<string, string>> = {};
    for (const [lang, fields] of Object.entries(otherLangs)) {
      const hasContent = Object.values(fields).some((v) => v?.trim());
      if (hasContent) {
        cleaned[lang] = fields;
      }
    }
    patch.translations = Object.keys(cleaned).length > 0 ? cleaned : undefined;

    onChange(patch);
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Тема письма (основной язык)" name="subject">
          <Input
            value={action.subject ?? ''}
            onChange={(e) => onChange({ subject: e.target.value || undefined })}
            placeholder="Тема письма"
          />
        </FormField>

        <FormField label="Тело письма (основной язык)" name="body" className="md:col-span-2">
          <Textarea
            value={action.body ?? ''}
            onChange={(e) => onChange({ body: e.target.value || undefined })}
            rows={4}
            placeholder="HTML или текст письма"
          />
        </FormField>
      </div>

      {showTranslations && (
        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Переводы inline текстов</p>
            <LanguageSwitcher
              languages={languages}
              activeLanguage={activeLanguage}
              onLanguageChange={setActiveLanguage}
              fields={TRANSLATION_FIELDS}
              value={translationsData}
              variant="inline"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Если для языка пользователя есть перевод — будет использован он. Иначе — основной текст выше.
          </p>
          <TranslationsForm
            fields={TRANSLATION_FIELDS}
            value={translationsData}
            onChange={handleTranslationsChange}
            languages={languages}
            activeLanguage={activeLanguage}
            onLanguageChange={setActiveLanguage}
          />
        </div>
      )}
    </div>
  );
}
